import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import AddressForm from './AddressForm';
import './AddressBook.css';

export default function AddressBook() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    loadAddresses();
  }, [isAuthenticated, navigate]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const user = await api.getUserProfile();
      setAddresses(user.addresses || []);
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingAddress) {
        await api.updateAddress(editingAddress._id, formData);
      } else {
        await api.addAddress(formData);
      }
      await loadAddresses();
      resetForm();
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    try {
      await api.deleteAddress(addressId);
      await loadAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      alert('Failed to delete address. Please try again.');
    }
  };

  const resetForm = () => {
    setEditingAddress(null);
    setShowForm(false);
  };

  const getAddressTypeIcon = (type) => {
    const icons = {
      home: '🏠',
      work: '🏢',
      other: '📍'
    };
    return icons[type] || '📍';
  };

  if (loading) {
    return (
      <div className="address-book-container">
        <div className="loading-state">
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="address-book-container">
      <div className="address-book-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Home
        </button>
        <div className="header-content">
          <h1>Address Book</h1>
          <button 
            className="btn primary add-address-btn"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add New Address
          </button>
        </div>
      </div>

      {showForm && (
        <AddressForm
          isOpen={showForm}
          onClose={resetForm}
          onSubmit={handleSubmit}
          editingAddress={editingAddress}
          userPhone={user?.phone}
        />
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="empty-addresses">
          <div className="empty-icon">📍</div>
          <h2>No addresses saved</h2>
          <p>Add an address to make checkout faster!</p>
          <button 
            className="btn primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Add Your First Address
          </button>
        </div>
      ) : (
        <div className="addresses-grid">
          {addresses.map((address) => (
            <div key={address._id} className="address-card">
              <div className="address-header">
                <div className="address-type">
                  <span className="type-icon">{getAddressTypeIcon(address.type)}</span>
                  <span className="type-label">{address.type || 'home'}</span>
                </div>
                <div className="address-actions">
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(address)}
                    title="Edit address"
                  >
                    ✏️
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(address._id)}
                    title="Delete address"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="address-content">
                <div className="address-name">{address.name}</div>
                {address.phone && (
                  <div className="address-phone">📞 {address.phone}</div>
                )}
                <div className="address-text">
                  {address.street}
                  {address.floor && `, Floor ${address.floor}`}
                  {address.landmark && `, ${address.landmark}`}
                  <br />
                  {address.area && `${address.area}, `}
                  {address.city}, {address.state} - {address.pincode}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

